import { ICoursesInPerson } from '../../interfaces/Website/Courses/CoursesInPerson/ICoursesInPerson'
import { IFormData } from '../../pages/Dashboard/Courses/CoursesOnline/CoursesOnlineModal'
import apiService from '../Api'
import { ICourse } from './interfaces'

/**
 * Pacote "Passaporte" na Eduzz (Monte seu Kit, ABRA-406). O multipart descarta array vazio, então
 * o marcador `package_course_ids_provided` diz à API que a lista veio, mesmo esvaziada.
 */
const appendPackage = (formData: FormData, data: IFormData) => {
  formData.append('eduzz_package_url', data.eduzz_package_url)
  formData.append('package_price', data.package_price == null ? '' : String(data.package_price))
  formData.append('package_course_ids_provided', '1')
  data.package_course_ids.forEach((courseId) => {
    formData.append('package_course_ids[]', String(courseId))
  })
}

export class CoursesOnlineService {
  async list(
    search: string,
    page: number,
    per_page: number
  ): Promise<{ total_filtered: number; data: ICoursesInPerson[] }> {
    return apiService
      .get('/course-recordeds', {
        params: {
          search,
          page,
          per_page
        }
      })
      .then((res) => res.data)
  }
  async getCourse(id: number): Promise<ICourse> {
    return apiService.get(`/course-recordeds/${id}`).then((res) => res.data)
  }

  async getKnowledgeAreas() {
    return apiService.get(`/courses/knowledge-areas`).then((res) => res.data)
  }

  async getDynamics() {
    return apiService.get(`/courses/dynamics`).then((res) => res.data)
  }

  async getObjectives() {
    return apiService.get(`/courses/objectives`).then((res) => res.data)
  }

  async getActiveCampaignLists() {
    return apiService.get(`/courses/active-campaign-lists`).then((res) => res.data.data)
  }

  /**
   * Fetches the recorded-courses installment configuration (PMT rate + nper)
   * from the public API. Single source of truth for the live preview — keeps
   * the front in sync with config/pricing.php on the back.
   */
  async getRecordedInstallmentConfig(): Promise<{ monthly_rate: number; installments: number }> {
    return apiService.get('/website/courses/recorded/installment-config').then((res) => res.data.data)
  }

  async create(data: IFormData) {
    const formData = new FormData()

    formData.append('name', data.name)
    if (data.thumbnail) {
      formData.append('thumbnail', data.thumbnail)
    }
    formData.append('course_link', data.course_link)
    formData.append('eduzz_url', data.eduzz_url || '')

    formData.append('full_price_recorded', String(data.full_price_recorded ?? 0))
    formData.append(
      'discount_percentage_recorded',
      data.discount_percentage_recorded.replace('.', '').replace(',', '.')
    )
    formData.append('duration_in_hours', String(data.duration_in_hours))
    formData.append('promotional_bar', data.promotional_bar)
    formData.append('duration_in_months', String(data.duration_in_months))

    if (data.cta_text_recorded) {
      formData.append('cta_text_recorded', data.cta_text_recorded)
    }

    if (data.cronometer_title) {
      formData.append('cronometer_title', data.cronometer_title)
    }

    if (data.promotional_bar === '1') {
      formData.append('cronometer_type', data.cronometer_type!)

      if (data.cronometer_type === 'end_date') {
        formData.append('cronometer_end_date', data.cronometer_end_date!)
      }

      if (data.cronometer_type === 'time') {
        formData.append('cronometer_time', data.cronometer_time!)
      }
    }

    data.knowledge_area_ids.forEach((area) => {
      formData.append('knowledge_area_ids[]', area)
    })
    data.objective_ids.forEach((objective) => {
      formData.append('objective_ids[]', objective)
    })
    data.dynamic_ids.forEach((dynamic) => {
      formData.append('dynamic_ids[]', dynamic)
    })
    data.active_campaign_lists.forEach((campaignId) => {
      formData.append('active_campaign_lists[]', campaignId)
    })
    appendPackage(formData, data)

    return apiService.post('/course-recordeds', formData).then((res) => res.data)
  }

  async update(id: number, data: IFormData) {
    const formData = new FormData()

    formData.append('_method', 'PUT')
    formData.append('name', data.name)
    if (data.thumbnail && data.thumbnail instanceof File) {
      formData.append('thumbnail', data.thumbnail)
    }
    formData.append('course_link', data.course_link)
    formData.append('eduzz_url', data.eduzz_url || '')
    formData.append('full_price_recorded', String(data.full_price_recorded ?? 0))
    formData.append(
      'discount_percentage_recorded',
      data.discount_percentage_recorded.replace('.', '').replace(',', '.')
    )
    formData.append('duration_in_hours', String(data.duration_in_hours))
    formData.append('promotional_bar', data.promotional_bar)
    formData.append('duration_in_months', String(data.duration_in_months))

    if (data.cta_text_recorded !== undefined) {
      formData.append('cta_text_recorded', data.cta_text_recorded)
    }

    // Sempre enviado: o campo vazio chega como null na API e apaga o título.
    formData.append('cronometer_title', data.cronometer_title)

    if (data.promotional_bar === '1') {
      formData.append('cronometer_type', data.cronometer_type!)

      if (data.cronometer_type === 'end_date') {
        formData.append('cronometer_end_date', data.cronometer_end_date!)
      }

      if (data.cronometer_type === 'time') {
        formData.append('cronometer_time', data.cronometer_time!)
      }
    }

    data.knowledge_area_ids.forEach((area) => {
      formData.append('knowledge_area_ids[]', area)
    })
    data.objective_ids.forEach((objective) => {
      formData.append('objective_ids[]', objective)
    })
    data.dynamic_ids.forEach((dynamic) => {
      formData.append('dynamic_ids[]', dynamic)
    })
    data.active_campaign_lists.forEach((campaignId) => {
      formData.append('active_campaign_lists[]', campaignId)
    })
    appendPackage(formData, data)

    return apiService.post(`/course-recordeds/${id}`, formData).then((res) => res.data)
  }

  async updateEnable(id: number, enabled: boolean) {
    return apiService.put(`/course-recordeds/${id}`, {
      enabled: enabled ? '1' : '0'
    })
  }

  async updateFeatured(id: number, isFeatured: boolean) {
    return apiService.put(`/course-recordeds/${id}`, {
      is_featured: isFeatured ? '1' : '0'
    })
  }

  async updateShowOnHomepage(id: number, showOnHomepage: boolean) {
    return apiService.put(`/course-recordeds/${id}`, {
      show_on_homepage: showOnHomepage ? '1' : '0'
    })
  }

  async delete(id: number) {
    return apiService.delete(`/course-recordeds/${id}`)
  }

  async listForSelect(): Promise<{ id: number; name: string }[]> {
    return apiService.get('/course-recordeds/select').then((res) => res.data)
  }

  async bulkUpdateCoupons(payload: {
    course_ids: number[]
    coupon: string | null
    cta_text?: string
  }): Promise<{ message: string; updated: number; skipped: number }> {
    return apiService.put('/course-recordeds/bulk-coupons', payload).then((res) => res.data)
  }
}
